// File: src/utils/countryGroups.js
// The country list, cut into the groups a picker shows and sorted for reading.
//
// Two things here are not obvious and are the whole reason this is a module
// rather than a loop inside the select.
//
// SORTING HAPPENS AT RENDER TIME, PER LANGUAGE. The names on screen come from
// Intl.DisplayNames, not from the English `name` in the data, so a list sorted
// once when the file was written is sorted for exactly one of the three
// languages. Sorting by English and showing it to a Swedish reader gives
// "Australien, Osterrike, Danmark, Sverige, Schweiz, Zimbabwe" - because O with
// diaeresis sorts after Z in Swedish, and nowhere near it in English.
//
// GROUP ORDER IS ALSO PART OF IT. Nordics first because that is where the
// racing is, Antarctica last because it is there for completeness; the six in
// between are sorted by their own translated labels, since "Afrikka, Aasia" and
// "Africa, Asia" and "Afrika, Asien" do not order the same way either.

import { countries, CONTINENT_FIRST, CONTINENT_LAST, CONTINENTS } from '../data/countries';
import { countryName } from './countryNames';

/** The translation key for a group, e.g. 'northAmerica' -> 'continentNorthAmerica'. */
export const groupLabelKey = (continent) =>
  `continent${continent[0].toUpperCase()}${continent.slice(1)}`;

// Building the sorted groups means 250 Intl.DisplayNames lookups plus a
// collation pass, and eight selects may be on one page. There are only ever
// three languages, and the data never changes, so the result is kept for the
// life of the page - the same trick countryNames.js uses for its formatters.
const cache = new Map();

const collatorFor = (language) => {
  try {
    return new Intl.Collator(language).compare;
  } catch {
    // No Intl.Collator, or a locale it will not take. localeCompare is worse
    // but is never absent, and a slightly odd order beats a blank picker.
    return (a, b) => String(a).localeCompare(String(b));
  }
};

/**
 * The country list as ordered groups, each already sorted for this language.
 *
 * `translate` is the t() from LanguageContext. It is needed here rather than in
 * the caller because the middle groups are ordered by their TRANSLATED labels -
 * ordering them by the raw 'europe'/'africa' keys would be the same bug as
 * sorting the countries by their English names.
 *
 * Returns [{ continent, labelKey, label, countries: [{ ...country, label }] }].
 */
export const groupedCountries = (language, translate) => {
  if (cache.has(language)) return cache.get(language);

  const compare = collatorFor(language);

  const groups = CONTINENTS.map(continent => {
    const labelKey = groupLabelKey(continent);
    return {
      continent,
      labelKey,
      label: translate(labelKey),
      countries: countries
        .filter(country => country.continent === continent)
        .map(country => ({ ...country, label: countryName(country, language) }))
        .sort((a, b) => compare(a.label, b.label))
    };
  });

  // Pinned ends, translated middle. `rank` keeps the sort total rather than
  // relying on comparator side effects.
  const rank = (continent) =>
    continent === CONTINENT_FIRST ? 0 : continent === CONTINENT_LAST ? 2 : 1;

  groups.sort((a, b) =>
    rank(a.continent) - rank(b.continent) || compare(a.label, b.label));

  cache.set(language, groups);
  return groups;
};

export default groupedCountries;
